import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, Plus, Trash, PencilSimple, ArrowLeft, Key, WarningCircle } from "@phosphor-icons/react";
import EmptyState from "@/components/EmptyState";

/**
 * v1.22.0 — Super Admin management page.
 * Only visible to the pre-seeded super admin (user.is_super_admin === true).
 * Regular admins hitting /admin/settings/admins get a locked-out card.
 */
export default function Admins() {
    const { user } = useAuth();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openCreate, setOpenCreate] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ email: "", name: "", password: "", phone: "", perms: { can_create_customer_logins: true } });

    const isSuper = user?.is_super_admin;

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admins");
            setAdmins(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { if (isSuper) load(); else setLoading(false); }, [isSuper]);

    const openNew = () => {
        setEditing(null);
        setForm({ email: "", name: "", password: "", phone: "", perms: { can_create_customer_logins: true } });
        setOpenCreate(true);
    };
    const openEdit = (a) => {
        setEditing(a);
        setForm({
            email: a.email, name: a.name, password: "", phone: a.phone || "",
            perms: { can_create_customer_logins: !!a.admin_permissions?.can_create_customer_logins },
        });
        setOpenCreate(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editing) {
                const body = { name: form.name, permissions: form.perms };
                if (form.password) body.password = form.password;
                await api.patch(`/admins/${editing.id}`, body);
                toast.success(`${form.name || form.email} updated`);
            } else {
                if (!form.email || !form.password) { toast.error("Email and password required"); setSaving(false); return; }
                await api.post("/admins", {
                    email: form.email, password: form.password, name: form.name || form.email.split("@")[0],
                    phone: form.phone, permissions: form.perms,
                });
                toast.success(`Admin ${form.email} created`);
            }
            setOpenCreate(false);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const del = async (a) => {
        if (!window.confirm(`Delete admin ${a.email}?\n\nThey will no longer be able to sign in. This cannot be undone.`)) return;
        try {
            await api.delete(`/admins/${a.id}`);
            toast.success(`${a.email} deleted`);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const togglePerm = async (a, key, val) => {
        try {
            await api.patch(`/admins/${a.id}`, { permissions: { [key]: val } });
            setAdmins((prev) => prev.map((x) => x.id === a.id
                ? { ...x, admin_permissions: { ...(x.admin_permissions || {}), [key]: val } }
                : x));
            toast.success(`Updated · ${key} → ${val ? "ON" : "OFF"}`);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    if (!isSuper) {
        return (
            <div className="mx-auto max-w-lg space-y-4 py-10 text-center" data-testid="admins-locked">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 text-rose-700">
                    <WarningCircle size={28} weight="fill" />
                </div>
                <h1 className="font-display text-2xl font-bold text-slate-900">Super Admin only</h1>
                <p className="text-sm text-slate-500">This page manages admin accounts and their permissions. Ask the Super Admin (info@teleadmin.in) if you need access.</p>
                <Link to="/admin" className="inline-block text-sm font-semibold text-blue-700 hover:underline">← Back to Dashboard</Link>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admins-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <Link to="/admin" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800">
                        <ArrowLeft size={12} /> Back to Dashboard
                    </Link>
                    <div className="mt-1 flex items-center gap-2">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-blue-700 text-white shadow-md">
                            <ShieldCheck size={20} weight="fill" />
                        </div>
                        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">Admin Accounts</h1>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">Create admins, toggle their permissions, and reset passwords. Super Admin has every permission by default.</p>
                </div>
                <Button onClick={openNew} data-testid="add-admin-btn" className="bg-blue-600 text-white hover:bg-blue-700">
                    <Plus size={14} className="mr-1.5" /> New admin
                </Button>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                {loading ? (
                    <div className="p-6 text-sm text-slate-500">Loading…</div>
                ) : admins.length === 0 ? (
                    <div className="p-6"><EmptyState icon={ShieldCheck} title="No admins yet" description="Add your first admin from the button above." /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm" data-testid="admins-table">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-4 py-2 text-left">Admin</th>
                                    <th className="px-3 py-2 text-left">Created</th>
                                    <th className="px-3 py-2 text-center">Can create customer logins</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {admins.map((a) => (
                                    <tr key={a.id} data-testid={`admin-row-${a.id}`} className="hover:bg-slate-50">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="font-semibold text-slate-900">{a.name || a.email.split("@")[0]}</div>
                                                {a.is_super_admin && (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-indigo-100 to-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-800" data-testid={`admin-super-${a.id}`}>
                                                        <ShieldCheck size={10} weight="fill" /> Super
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-slate-500">{a.email}</div>
                                            {a.phone && <div className="text-[10px] text-slate-400">📱 {a.phone}</div>}
                                        </td>
                                        <td className="px-3 py-3 whitespace-nowrap text-xs text-slate-500">
                                            {a.created_at ? (() => { const d = new Date(a.created_at); const p = (n) => String(n).padStart(2, "0"); return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`; })() : "—"}
                                            {a.created_by && <div className="text-[10px] text-slate-400">by {a.created_by}</div>}
                                        </td>
                                        <td className="px-3 py-3 text-center">
                                            {a.is_super_admin ? (
                                                <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700">Always on</span>
                                            ) : (
                                                <Switch
                                                    checked={!!a.admin_permissions?.can_create_customer_logins}
                                                    onCheckedChange={(v) => togglePerm(a, "can_create_customer_logins", v)}
                                                    data-testid={`admin-perm-clogins-${a.id}`}
                                                />
                                            )}
                                        </td>
                                        <td className="px-3 py-3 text-right">
                                            <div className="flex justify-end gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => openEdit(a)}
                                                    data-testid={`edit-admin-${a.id}`}
                                                    className="rounded p-1.5 text-slate-500 hover:bg-blue-50 hover:text-blue-700"
                                                    title="Edit / reset password"
                                                >
                                                    <PencilSimple size={14} />
                                                </button>
                                                {!a.is_super_admin && a.id !== user.id && (
                                                    <button
                                                        type="button"
                                                        onClick={() => del(a)}
                                                        data-testid={`delete-admin-${a.id}`}
                                                        className="rounded p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-700"
                                                        title="Delete admin"
                                                    >
                                                        <Trash size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Dialog open={openCreate} onOpenChange={setOpenCreate}>
                <DialogContent data-testid="admin-dialog" className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display">{editing ? `Edit ${editing.email}` : "Create new admin"}</DialogTitle>
                        <DialogDescription>{editing ? "Update name, reset password, or toggle permissions." : "The new admin can sign in immediately with the email + password you set here."}</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-3">
                        <div>
                            <Label>Email</Label>
                            <Input data-testid="admin-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} className="mt-1" placeholder="new-admin@company.com" />
                        </div>
                        <div>
                            <Label>Name</Label>
                            <Input data-testid="admin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="Display name" />
                        </div>
                        <div>
                            <Label className="flex items-center gap-1"><Key size={12} /> {editing ? "New password (leave blank to keep current)" : "Password"}</Label>
                            <Input data-testid="admin-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" placeholder="min 6 characters" />
                        </div>
                        {!editing && (
                            <div>
                                <Label>Phone (optional)</Label>
                                <Input data-testid="admin-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" placeholder="10-digit mobile" />
                            </div>
                        )}
                        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Permissions</div>
                            <label className="mt-2 flex items-start gap-2 text-sm">
                                <Switch checked={form.perms.can_create_customer_logins} onCheckedChange={(v) => setForm({ ...form, perms: { ...form.perms, can_create_customer_logins: v } })} data-testid="admin-form-perm-clogins" />
                                <div>
                                    <div className="font-medium text-slate-900">Create customer logins</div>
                                    <div className="text-xs text-slate-500">Lets this admin generate portal accounts for parties.</div>
                                </div>
                            </label>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpenCreate(false)}>Cancel</Button>
                            <Button type="submit" disabled={saving} data-testid="save-admin-btn" className="bg-blue-600 text-white hover:bg-blue-700">
                                {saving ? "Saving…" : (editing ? "Save changes" : "Create admin")}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
